import { isEmpty } from "lodash";
import { useLocation, useNavigate } from "react-router-dom";
import { app } from "router/routes";
import { useCallback, useContext } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { allocationBatteryFetch } from "store/middlewares/assessment.crud";
import { CreateAssessmentsContext } from "../../CreateAssessmentsContext";
import { toast } from "react-toastify";
import { toastConfig } from "config";

/**
 * Hook המנהל את הלוגיקה והמצב של כפתור הקצאת המבדקים.
 * מחזיר אובייקט הגדרות מוכן עבור רכיב Button.
 */
const useBatteryAllocationBtn = () => {
    const {
        learningGroups,
        selectedTestTakers,
        school,
        testTakersFlatList,
        preventDuplicateBatteries,
        testTakerFilters,
        semester,
        year,
        isSchoolsGroup,
        selectedAssessmentDefs,
        assessmentFilters,
        hasLearningUnit, learningUnit,
        isPractice
    } = useContext(CreateAssessmentsContext)

    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const { pathname } = useLocation()

    // ניהול המוטציה מול השרת באמצעות React Query
    const { mutate: allocationBattery, isPending } = useMutation({
        mutationFn: allocationBatteryFetch,
        onSuccess: () => {
            // ריפרש לשאילתות רלוונטיות כדי לעדכן את רשימת המבדקים במערכת
            // queryClient.invalidateQueries({que});
            toast.success('ההקצאה בוצעה בהצלחה', toastConfig);
            // ניווט רק אחרי הצלחה
            if (pathname == app.createPracticeAssessments)
                navigate(app.practiceAssessments);
            else
                navigate(app.performAssessments);
        },
        onError: (error) => {
            const { data } = error?.response || {}
            const msg =
                data?.detail || data?.error ||
                'אופס, משהו השתבש בהקצאה';
            toast.error(msg, toastConfig);
        }
    });

    /**
    * פונקציית עזר לארגון רשימת התלמידים והשיוכים שלהם לפני שליחה לשרת.
    * עוברת על מבנה הנתונים הקיים ומחברת בין תלמיד שנבחר לבין יחידת הלימוד שלו.
    * הפונקציה מנקה את הקידומות (Prefixes) מהמזהים כדי שהשרת יקבל IDs מספריים.
    */
    const getOrganizedTestTakers = () => {
        const results = [];
        // חובה לבחור לפחות תלמיד אחד
        if (!selectedTestTakers) return results;

        // פונקציית עזר פנימית לניקוי הקידומת מה-ID (למשל "student-123" הופך ל-"123")
        const getRawId = (prefixedId) => {
            if (!prefixedId) return null;
            const parts = prefixedId.toString().split('_');
            return parts.length > 1 ? parts[1] : parts[0];
        };

        // כשבחרנו כבר יחידת למידה מסויימת, הגישה היא רק לתלמידים של אותה יחידה
        if (hasLearningUnit) {
            testTakersFlatList?.data?.forEach(student => {
                if (selectedTestTakers[student.id]) {
                    results.push({
                        test_taker_id: student.id,
                        learning_unit_id: learningUnit
                    });
                }
            });
        }
        else if (isSchoolsGroup) {
            // --- מבנה בית ספרי (3 רמות) ---
            if (!school?.data) return results;
            school?.data.forEach(grade => {
                const groups = grade.units || grade.learning_groups || [];
                groups.forEach(unit => {
                    unit.test_takers?.forEach(student => {
                        if (selectedTestTakers[student.id]) {
                            results.push({
                                test_taker_id: getRawId(student.id),
                                learning_unit_id: getRawId(unit.id)
                            });
                        }
                    });
                });
            });
        } else {
            // --- מבנה קבוצות חוץ (2 רמות) ---
            if (!learningGroups?.data) return results;
            learningGroups?.data.forEach(unit => {
                unit.test_takers?.forEach(student => {
                    if (selectedTestTakers[student.id]) {
                        results.push({
                            test_taker_id: getRawId(student.id),
                            learning_unit_id: getRawId(unit.id)
                        });
                    }
                });
            });
        }

        return results;
    };

    const createAssessments = useCallback(() => {
        // 1. ארגון הנתונים מהבחירה
        const organizedTestTakers = getOrganizedTestTakers(
            school?.data,           // למקרה מדובר בלקוח בית ספרי
            learningGroups?.data,   // למקרה שמדובר בלקוח חוץ בית ספרי
            testTakersFlatList?.data, // למקרה שמדובר בשיוך ברמת יחידת למידה
            selectedTestTakers,
            testTakerFilters?.affiliation,
            hasLearningUnit
        );

        // 2. ולידציה בסיסית - לוודא שנבחרו תלמידים ומבדקים
        if (isEmpty(selectedAssessmentDefs) || isEmpty(organizedTestTakers)) {
            toast.error('עליך לבחור לפחות תלמיד.ה אחד ומבדק אחד', toastConfig);
            return;
        }

        // 3. בניית ה-Payload לשליחה לשרת
        const payload = {
            test_takers: organizedTestTakers, // רשימת אובייקטים: [{test_taker_id, learning_unit_id}, ...]
            school_year: year?.id || year,
            semester_of_execution: semester?.id || semester,
            prevent_duplicate_batteries: preventDuplicateBatteries,
            is_practice: isPractice
        };

        // טיפול במבדקים בודדים מול סוללה (Battery)
        if (assessmentFilters.battery) {
            payload.battery = assessmentFilters.battery.id;
        } else {
            payload.assessment_defs = selectedAssessmentDefs.map(ass => ({
                id: ass.id
                // בעבר העברנו גם layout_type בנתיים בטלתי אותו 
            }));
        }

        // 4. ביצוע המוטציה
        allocationBattery(payload);

        // --- תיעוד: העברנו את הטיפול בלוגיקת התאריכים והסמסטרים לשרת בלבד ---
        // בעבר בוצע כאן חישוב: let school_year = year ...

    }, [
        school?.data,
        learningGroups?.data,
        selectedTestTakers,
        testTakerFilters?.affiliation,
        selectedAssessmentDefs,
        year,
        semester,
        preventDuplicateBatteries,
        assessmentFilters,
        allocationBattery
    ]);

    return { action: createAssessments, isPending };
};

export default useBatteryAllocationBtn;